// Toast Messages Component

const SELECTORS = {
    toast: '#js-toast',
    messageContainer: '.js-toast-message',
    actionButton: '.js-toast-action'
}

const CLASSES = {
    active: 'toast--active',
    idle: 'toast--idle',
    type: {
        info: 'toast--info',
        warning: 'toast--warning',
        error: 'toast--error',
        success: 'toast--success'
    }
}

class Toast {
    constructor(domEl) {
        this.element = domEl
        if (!this.element) {
            console.error('No DOM element to display toasts.')
            return
        }

        this.messageContainer = this.element.querySelector(
            SELECTORS.messageContainer
        )
        this.actionButton = this.element.querySelector(SELECTORS.actionButton)
        this.queuedMessages = []

        this.reset()

        if (window.djMessages) {
            this.show(window.djMessages)
        }
    }

    // Loop over an array of messages
    // and set them up for toasting
    show(messages) {
        if (Array.isArray(messages) && messages.length > 0) {
            for (const msg of messages) {
                if (typeof msg.message !== 'undefined') {
                    this.loadMessage(msg)
                }
            }
        }
    }

    // load a message object and either display it
    // or feed it into the queue if another toast is already open
    loadMessage(msg) {
        if (this.isActive) {
            this.queuedMessages.push(msg)
        } else {
            this.isActive = true
            this.data = msg
            this.action = () => this.dismiss()

            // if a timeout was provided, fire the action after it is done
            if (typeof msg.timeout === 'number') {
                this.timeoutId = setTimeout(this.action, msg.timeout)
            }

            this.display()
        }
    }

    // display the toast on the page
    display() {
        if (!this.element) {
            console.error('No DOM element to display toasts.')
            return
        }
        this.element.setAttribute('aria-hidden', 'true')
        this.actionButton.addEventListener('click', this.action)

        this.setType()
        this.setActionToHidden(false)

        this.messageContainer.textContent = this.data.message
        this.element.classList.remove(CLASSES.idle)
        this.element.classList.add(CLASSES.active)
        this.element.setAttribute('aria-hidden', 'false')
    }

    // dismiss an open toast, optionally fire callback
    dismiss() {
        if (this.isActive) {
            if (typeof this.data.callback === 'function') {
                this.data.callback()
            }
            this.hide()
        }
    }

    // hide an open toast and reset its attributes and content
    hide() {
        if (!this.element) {
            console.error('No DOM element to display toasts.')
            return
        }
        this.element.classList.remove(CLASSES.active)

        // wait for the slideout animation to finish first
        setTimeout(() => {
            this.element.classList.add(CLASSES.idle)
            this.element.setAttribute('aria-hidden', 'true')
            this.messageContainer.textContent = ''

            Object.keys(CLASSES.type).forEach(key => {
                this.element.classList.remove(CLASSES.type[key])
            })

            if (!this.actionButton.getAttribute('aria-hidden')) {
                this.actionButton.removeEventListener('click', this.action)
            }

            this.reset()
            this.checkQueue()
        }, 450)
    }

    // Set the modifier class representing the type of message
    // must be one of 'info|warning|error|success'
    setType() {
        const type = this.data.type
        if (type && typeof CLASSES.type[type] !== 'undefined') {
            if (!this.element) {
                console.error('No DOM element to display toasts.')
                return
            }
            this.element.classList.add(CLASSES.type[type])
        } else {
            console.warn(`Unknown message type '${type}'`)
        }
    }

    // toggle the hidden attr on the button,
    // so it wont receive tab focus when inactive
    setActionToHidden(hide) {
        if (hide) {
            this.actionButton.setAttribute('hidden', 'true')
        } else {
            this.actionButton.removeAttribute('hidden')
        }
    }

    // Reset values to initial and clear running timeouts
    reset() {
        this.isActive = false
        this.data = undefined
        this.action = undefined
        this.setActionToHidden(true)

        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = undefined
        }
    }

    // check if we have another message wating,
    // and if so, load it up
    checkQueue() {
        if (this.queuedMessages.length > 0) {
            this.loadMessage(this.queuedMessages.shift())
        }
    }
}

const toastEl = document.querySelector(SELECTORS.toast)
const toast = new Toast(toastEl)
export default toast
